import { attachAutoTracking } from "./autotrack";
import { SnipcatClient } from "./client";
import type {
  SnipcatAttribution,
  SnipcatConfig,
  SnipcatLeadInput,
  SnipcatLeadResult,
  SnipcatPageStats,
  SnipcatPatch,
  SnipcatPlatform,
  SnipcatValues,
} from "./types";

export {
  SnipcatClient,
  DEFAULT_STORAGE_PREFIX,
  sessionStorageKey,
  readStoredSessionHash,
} from "./client";
export { sanitizeValues, eventKey } from "./values";
export { captureAttribution, loadAttribution } from "./attribution";
export type {
  SnipcatAttribution,
  SnipcatConfig,
  SnipcatLeadInput,
  SnipcatLeadResult,
  SnipcatPageStats,
  SnipcatPatch,
  SnipcatPlatform,
  SnipcatValues,
};

/**
 * The instance a page gets by default. Everything a customer's site needs is
 * on it; `new SnipcatClient()` exists for the rare page that tracks two
 * funnels at once.
 */
export const snipcat = new SnipcatClient();

/**
 * Set once the declarative hooks are wired. React effects and hot reloads call
 * init() more than once, and a second set of listeners would double every
 * click and re-time every step.
 */
let autoTrackAttached = false;

/**
 * Starts tracking. Safe to call twice - the second call updates the config and
 * does not duplicate the listeners.
 */
export const init = (config: SnipcatConfig): SnipcatClient => {
  snipcat.init(config);
  if (config.autoTrack !== false && !autoTrackAttached) {
    autoTrackAttached = true;
    attachAutoTracking(snipcat, { scrollDepth: config.scrollDepth });
  }
  return snipcat;
};

export const track = (values: Record<string, unknown>): void =>
  snipcat.track(values);
export const trackEvent = (
  event: string,
  variant?: string | null,
  value?: string | number,
): void => snipcat.trackEvent(event, variant, value);
export const trackStep = (stepId: string | null): void =>
  snipcat.trackStep(stepId);
export const submitLead = (
  input: SnipcatLeadInput,
): Promise<SnipcatLeadResult> => snipcat.submitLead(input);
export const claim = (platform: SnipcatPlatform): Promise<boolean> =>
  snipcat.claim(platform);
export const getSessionHash = (): string | null => snipcat.getSessionHash();
export const adoptSession = (sessionHash: string): void =>
  snipcat.adoptSession(sessionHash);
export const flush = (): void => snipcat.flush();
export const reset = (): void => snipcat.reset();

/**
 * Script-tag bootstrap.
 *
 * <script src="…/snipcat.js"
 *         data-api-base="https://app.snipcat.ai/api"
 *         data-lead-hash="…"
 *         data-form-hash="…"
 *         data-auto-init
 *         data-scroll-depth="25,50,75,100"></script>
 *
 * `data-scroll-depth` is optional: a comma separated list of percentages, or
 * "false" to record no scroll depth at all.
 *
 * Without `data-auto-init` nothing starts and the page calls Snipcat.init()
 * itself - which is what a site with a cookie banner wants, so tracking can
 * wait for consent.
 */
const parseScrollDepth = (
  raw: string | undefined,
): SnipcatConfig["scrollDepth"] => {
  if (raw === undefined || raw === "") {
    return undefined;
  }
  if (raw === "false") {
    return false;
  }
  const list = raw
    .split(",")
    .map((item) => Number(item.trim()))
    .filter((item) => Number.isFinite(item));
  return list.length ? list : undefined;
};

const autoInitFromScriptTag = (): void => {
  if (typeof document === "undefined") {
    return;
  }
  const script =
    (document.currentScript as HTMLScriptElement | null) ??
    document.querySelector<HTMLScriptElement>(
      "script[data-auto-init][src*='snipcat']",
    );
  if (!script || !script.hasAttribute("data-auto-init")) {
    return;
  }
  init({
    apiBase: script.dataset.apiBase || "https://app.snipcat.ai/api",
    leadHash: script.dataset.leadHash || null,
    formHash: script.dataset.formHash || null,
    autoTrack: script.dataset.autoTrack !== "false",
    autoStart: script.dataset.autoStart !== "false",
    scrollDepth: parseScrollDepth(script.dataset.scrollDepth),
    debug: script.hasAttribute("data-debug"),
  });
};

autoInitFromScriptTag();

export default snipcat;
